import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import ManageTable from '../ManageTable'
import { RequireModule, PageHeader, formatInr, BTN_PRIMARY } from './shared'

const PAYMENT_MODES = ['Cash', 'Cheque', 'UPI', 'NEFT/RTGS']

const EMPTY = {
  payment_id: '',
  payment_detail_id: '',
  user_id: '',
  donation_type_id: '',
  payment_amount: '',
  mode_of_payment: '',
  bank_name: '',
  branch_name: '',
  cheque_date: '',
  cheque_no: '',
  transaction_id: '',
}

function needsTxn(mode) {
  return mode === 'UPI' || mode === 'NEFT/RTGS'
}

function isPaidDonation(row) {
  return String(row?.is_payment_success || '').toUpperCase() === 'YES'
}

function toDateInput(value) {
  if (!value) return ''
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const t = Date.parse(s)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function DonationsPage() {
  const [rows, setRows] = useState([])
  const [users, setUsers] = useState([])
  const [types, setTypes] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [donorQuery, setDonorQuery] = useState('')
  const [donorOpen, setDonorOpen] = useState(false)

  const isEditing = Boolean(form.payment_id || form.payment_detail_id)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', { status: 'unpaid' }, 'donation/donationList')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load donations.')
      setRows([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    ;(async () => {
      const [u, t] = await Promise.all([
        GetAction('get', {}, 'user/userList'),
        GetAction('get', {}, 'donation_type/donationTypes'),
      ])
      if (u?.status === 'success' && Array.isArray(u.response)) setUsers(u.response)
      if (t?.status === 'success' && Array.isArray(t.response)) setTypes(t.response)
    })()
  }, [])

  const unpaidRows = useMemo(
    () =>
      rows
        .filter((row) => !isPaidDonation(row))
        .map((row, i) => ({ ...row, index: i + 1 })),
    [rows]
  )

  const selectedDonor = users.find((u) => String(u.id) === String(form.user_id))

  const donorMatches = useMemo(() => {
    const q = donorQuery.trim().toLowerCase()
    if (!q) return users.slice(0, 12)
    return users
      .filter((u) => {
        const name = (u.name || `${u.first_name || ''} ${u.last_name || ''}`).toLowerCase()
        const mobile = String(u.mobile || '').toLowerCase()
        const email = String(u.email_address || '').toLowerCase()
        return name.includes(q) || mobile.includes(q) || email.includes(q)
      })
      .slice(0, 12)
  }, [users, donorQuery])

  const closeForm = () => {
    setShowForm(false)
    setForm(EMPTY)
    setDonorQuery('')
    setDonorOpen(false)
  }

  const openNew = () => {
    setForm(EMPTY)
    setDonorQuery('')
    setDonorOpen(false)
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    const membershipId = row.membership_id != null ? String(row.membership_id) : ''
    const donor = users.find((u) => String(u.id) === membershipId)
    setForm({
      payment_id: row.payment_id != null ? String(row.payment_id) : '',
      payment_detail_id: row.payment_detail_id != null ? String(row.payment_detail_id) : '',
      user_id: membershipId,
      donation_type_id:
        row.donation_type_id != null && row.donation_type_id !== ''
          ? String(row.donation_type_id)
          : '',
      payment_amount: row.payment_amount ?? '',
      mode_of_payment: row.mode_of_payment || row.payment_mode || '',
      bank_name: row.bank_name || '',
      branch_name: row.branch_name || '',
      cheque_date: toDateInput(row.cheque_date),
      cheque_no: row.cheque_no || '',
      transaction_id: row.transaction_id || '',
    })
    setDonorQuery(
      donor
        ? donor.name || `${donor.first_name || ''} ${donor.last_name || ''}`.trim()
        : row.name || ''
    )
    setDonorOpen(false)
    setShowForm(true)
    setMessage(null)
  }

  const pickDonor = (user) => {
    setForm((p) => ({ ...p, user_id: String(user.id) }))
    setDonorQuery(user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim())
    setDonorOpen(false)
  }

  const onTypeChange = (e) => {
    const id = e.target.value
    const type = types.find((t) => String(t.donation_type_id) === String(id))
    setForm((p) => ({
      ...p,
      donation_type_id: id,
      payment_amount: type ? type.amount : p.payment_amount,
    }))
  }

  const donorHasKyc = (donor) => {
    const pan = String(donor?.pan_number ?? donor?.panNumber ?? '').trim()
    const aadhaar = String(donor?.adhar_card ?? donor?.adharCard ?? '').trim()
    return Boolean(pan && aadhaar)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.user_id) {
      setMessage({ error: true, text: 'Please select a donor.' })
      return
    }
    if (!form.donation_type_id) {
      setMessage({ error: true, text: 'Please select a donation type.' })
      return
    }
    // mode_of_payment is optional
    if (form.mode_of_payment === 'Cheque') {
      if (!form.bank_name || !form.branch_name || !form.cheque_date || !form.cheque_no) {
        setMessage({ error: true, text: 'Cheque fields are required.' })
        return
      }
    }
    if (needsTxn(form.mode_of_payment) && !form.transaction_id.trim()) {
      setMessage({ error: true, text: 'Transaction ID is required for UPI / NEFT/RTGS.' })
      return
    }

    // Cash donations require both PAN and Aadhaar on the donor profile
    if (form.mode_of_payment === 'Cash') {
      let donor = selectedDonor || users.find((u) => String(u.id) === String(form.user_id))
      if (!donorHasKyc(donor)) {
        const detail = await PostAction('post', { id: form.user_id }, 'user/userDetails')
        if (detail?.status === 'success' && detail.response) {
          donor = detail.response
        }
      }
      if (!donorHasKyc(donor)) {
        const msg =
          'PAN card and Aadhaar are required for Cash donations. Please update the donor profile.'
        window.alert(msg)
        setMessage({ error: true, text: msg })
        return
      }
    }

    setSaving(true)
    setMessage(null)
    const payload = {
      user_id: Number(form.user_id),
      donation_type_id: Number(form.donation_type_id),
      payment_amount: Number(form.payment_amount),
      mode_of_payment: form.mode_of_payment || '',
      bank_name: form.mode_of_payment === 'Cheque' ? form.bank_name : '',
      branch_name: form.mode_of_payment === 'Cheque' ? form.branch_name : '',
      cheque_date: form.mode_of_payment === 'Cheque' ? form.cheque_date : '',
      cheque_no: form.mode_of_payment === 'Cheque' ? form.cheque_no : '',
      transaction_id: needsTxn(form.mode_of_payment) ? form.transaction_id.trim() : '',
    }
    if (form.payment_id) payload.payment_id = Number(form.payment_id)
    if (form.payment_detail_id) payload.payment_detail_id = Number(form.payment_detail_id)

    const editing = isEditing
    const data = await PostAction('post', payload, 'donation/addDonation')
    if (data?.status === 'success') {
      setMessage({
        error: false,
        text: data.message || (editing ? 'Donation updated.' : 'Donation added.'),
      })
      closeForm()
      await load()
    } else {
      setMessage({
        error: true,
        text:
          data?.message ||
          data?.data?.message ||
          (editing ? 'Failed to update donation.' : 'Failed to add donation.'),
      })
    }
    setSaving(false)
  }

  const onDelete = async (row) => {
    const label = row.name || 'this donation'
    const amount = row.payment_amount != null ? ` (₹ ${row.payment_amount})` : ''
    if (!window.confirm(`Delete ${label}${amount}? This cannot be undone.`)) return

    setMessage(null)
    const payload = {}
    if (row.payment_id != null) payload.payment_id = Number(row.payment_id)
    if (row.payment_detail_id != null) payload.payment_detail_id = Number(row.payment_detail_id)

    const data = await PostAction('post', payload, 'donation/deleteDonation')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Donation deleted.' })
      await load()
    } else {
      setMessage({
        error: true,
        text: data?.message || data?.data?.message || 'Failed to delete donation.',
      })
    }
  }

  const baseColumns = [
    { key: 'index', label: '#', width: '60px' },
    { key: 'name', label: 'Name' },
    {
      key: 'mobile',
      label: 'Mobile',
      render: (row) => row.mobile || '—',
    },
    {
      key: 'type_name',
      label: 'Donation Type',
      render: (row) => row.type_name || row.donation_type_name || '—',
    },
    {
      key: 'payment_amount',
      label: 'Amount',
      render: (row) => formatInr(row.payment_amount),
    },
    { key: 'created_at', label: 'Date' },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDelete(row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ]

  const searchKeys = [
    'name',
    'mobile',
    'type_name',
    'donation_type_name',
    'payment_amount',
    'created_at',
  ]

  const mode = form.mode_of_payment
  const showCheque = mode === 'Cheque'
  const showTxn = needsTxn(mode)

  return (
    <RequireModule moduleKey="donations">
      <PageHeader
        title="Donations"
        subtitle="Unpaid and pending donation payments."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add Donation
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={baseColumns}
            rows={unpaidRows}
            searchKeys={searchKeys}
            emptyMessage="No unpaid donations found."
          />
        )}
      </div>

      <Modal
        show={showForm}
        onHide={closeForm}
        size="lg"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Donation' : 'Add Donation'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message?.error ? (
            <div className="manage-alert manage-alert-err mb-3">{message.text}</div>
          ) : null}
          <form id="manage-donation-form" onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="d_donor">
                  Donor
                </label>
                <div className="manage-typeahead">
                  <input
                    id="d_donor"
                    className="form-control"
                    autoComplete="off"
                    placeholder="Search name, mobile, or email…"
                    value={
                      donorOpen || !selectedDonor
                        ? donorQuery
                        : selectedDonor.name ||
                          `${selectedDonor.first_name || ''} ${selectedDonor.last_name || ''}`.trim()
                    }
                    onChange={(e) => {
                      setDonorQuery(e.target.value)
                      setForm((p) => ({ ...p, user_id: '' }))
                      setDonorOpen(true)
                    }}
                    onFocus={() => setDonorOpen(true)}
                    onBlur={() => setTimeout(() => setDonorOpen(false), 150)}
                    required={!form.user_id}
                  />
                  {donorOpen ? (
                    <div className="manage-typeahead-menu">
                      {donorMatches.length === 0 ? (
                        <div className="px-3 py-2 text-muted small">No matches</div>
                      ) : (
                        donorMatches.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            className="manage-typeahead-item"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickDonor(u)}
                          >
                            {u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim()}
                            {u.mobile ? ` · ${u.mobile}` : ''}
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="d_type">
                  Donation Type
                </label>
                <select
                  id="d_type"
                  className="form-control"
                  required
                  value={form.donation_type_id}
                  onChange={onTypeChange}
                >
                  <option value="">Select type</option>
                  {types.map((t) => (
                    <option key={t.donation_type_id} value={t.donation_type_id}>
                      {t.name} (₹ {t.amount})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="d_amount">
                  Amount
                </label>
                <input
                  id="d_amount"
                  type="number"
                  min="1"
                  className="form-control"
                  required
                  value={form.payment_amount}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, payment_amount: e.target.value }))
                  }
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="d_mode">
                  Mode of Payment
                </label>
                <select
                  id="d_mode"
                  className="form-control"
                  value={form.mode_of_payment}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      mode_of_payment: e.target.value,
                      transaction_id: needsTxn(e.target.value) ? p.transaction_id : '',
                    }))
                  }
                >
                  <option value="">Select mode (optional)</option>
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {showCheque ? (
                <>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="d_bank">
                      Bank Name
                    </label>
                    <input
                      id="d_bank"
                      className="form-control"
                      required
                      value={form.bank_name}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, bank_name: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="d_branch">
                      Branch
                    </label>
                    <input
                      id="d_branch"
                      className="form-control"
                      required
                      value={form.branch_name}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, branch_name: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="d_cdate">
                      Cheque Date
                    </label>
                    <input
                      id="d_cdate"
                      type="date"
                      className="form-control"
                      required
                      value={form.cheque_date}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, cheque_date: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="d_cno">
                      Cheque No
                    </label>
                    <input
                      id="d_cno"
                      className="form-control"
                      required
                      value={form.cheque_no}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, cheque_no: e.target.value }))
                      }
                    />
                  </div>
                </>
              ) : null}

              {showTxn ? (
                <div className="col-md-6 mb-3">
                  <label className="form-label" htmlFor="d_txn">
                    Transaction ID
                  </label>
                  <input
                    id="d_txn"
                    className="form-control"
                    required
                    value={form.transaction_id}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, transaction_id: e.target.value }))
                    }
                  />
                </div>
              ) : null}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeForm}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="manage-donation-form"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : isEditing ? 'Update Donation' : 'Save Donation'}
          </button>
        </Modal.Footer>
      </Modal>
    </RequireModule>
  )
}

export default DonationsPage
